/**
 * Adding, editing and removing behaviors, as one undo step per call.
 *
 * A behavior's data is the object's authored data, and these operations
 * write it as given, whatever state or keyframe shows. A state reaches a
 * behavior only through a field bound to a property, and a patch that keeps
 * the binding keeps it following the state; a `Properties` commit returns
 * that object to Base (`PropertyResolver.handleCommit`), so its new data
 * shows too. To put a value into the showing state instead, a host calls
 * `captureStateValue`, as the states panels do.
 *
 * Input from a tool call is checked before anything changes: `Properties`
 * data is fitted to the format (`sanitizePropertiesData`), and a
 * `BehaviorGraph` must run once its macros are lowered (`assertAuthoredGraph`).
 */

import type { SceneObject } from '../../core/SceneObject';
import type { TraceBehavior } from '../../behaviors/tracebehavior';
import type { BehaviorConstructor, DataOf } from '../../behaviors/BehaviorRegistry';
import type { BehaviorGraphData } from '../../scenedata/behaviors/behaviorgraphdata';
import { createDefaultData } from '../../scenedata/schemas/defaults';
import { Properties } from '../../behaviors/properties';
import { BehaviorGraph } from '../../behaviors/behaviorgraph/behaviorgraph';
import { AddBehaviorCommand } from '../undo/AddBehaviorCommand';
import { BehaviorCommitCommand } from '../undo/BehaviorCommitCommand';
import { RemoveBehaviorCommand } from '../undo/RemoveBehaviorCommand';
import { dispatchBehaviorChanged } from '../undo/editor-events';
import { assertAuthoredGraph, sanitizePropertiesData } from './behaviorInput';
import { editRefusal, prepareBatch, refuse } from './checks';
import { bundle, rollBack } from './steps';
import type { UndoCommand } from '../undo/UndoCommand';
import type { AuthoringPorts, BatchRefusal, BatchResult, Refusal } from './types';

/** A behavior to add, with the fields to set over its schema's defaults. */
export interface BehaviorAddEntry<T extends TraceBehavior = TraceBehavior> {
    object: SceneObject;
    type: BehaviorConstructor<T>;
    data?: Partial<Omit<DataOf<T>, '@type'>>;
}

/** A partial patch to a behavior: each top-level field replaces the one it names, and the rest keep their value. */
export interface BehaviorCommitEntry<T extends TraceBehavior = TraceBehavior> {
    behavior: T;
    patch: Partial<DataOf<T>>;
    /**
     * Names the field a stream of edits changes, such as a slider's. When the
     * batch's step is this one commit, pushing it right after a step with the
     * same key on the same behavior merges the two into one undo entry.
     */
    fieldKey?: string;
}

/** What fitting an entry's input to the format dropped, by the entry's index in the batch. */
export interface BatchWarning {
    entry: number;
    message: string;
}

/**
 * What an add or commit did. `applied` holds each applied entry's behavior;
 * `warnings` leaves out the entries that were refused.
 */
export type BehaviorBatchResult = BatchResult<TraceBehavior> & { orphaned: string[]; warnings: BatchWarning[] };

/**
 * Add each behavior, in the order given, as one undo step. An object that
 * already has a behavior of the type keeps it as it is: the entry is applied
 * and records nothing (`applied` holds the behavior it has), so a repeated
 * add is harmless. To change that behavior, commit to it. The entry's data
 * is still checked as input, below.
 *
 * An object inside a placed reference copies the reference into the scene
 * first, in the same step (`orphaned`, as for `deleteObjects`). A deleted
 * object, or one whose copy cannot be made, is refused on its own; while the
 * scene plays or an undo runs, every entry is. Asked again once the adds
 * have loaded: if play started, an undo began or an object was deleted
 * meanwhile, the batch is taken back and every entry refused.
 *
 * Data that is not an object or that names another `@type`, `Properties`
 * data with a declaration whose value is not a literal of its type, and a
 * `BehaviorGraph` that cannot run throw before anything changes, in any
 * mode. A fault while adding throws after the batch is taken back.
 */
export async function addBehaviors(
    entries: readonly BehaviorAddEntry[],
    ports: AuthoringPorts = {},
): Promise<BehaviorBatchResult> {
    const warnings: BatchWarning[] = [];
    const inputs = entries.map((entry, index) => {
        const data = fitInput(entry.type.behaviorType, entry.object, entry.data ?? {}, index, warnings);
        if (entry.type === BehaviorGraph) assertAuthoredGraph(createDefaultData<BehaviorGraphData>(BehaviorGraph.behaviorType, data));
        return { object: entry.object, type: entry.type, data };
    });
    if (inputs.length === 0) return { applied: [], refused: [], command: null, orphaned: [], warnings };
    const { ready, refused, steps, orphaned } = await prepareBatch(inputs, ports, {
        changes: ({ object, type }) => !object.getBehavior(type),
    });

    const applied: TraceBehavior[] = [];
    const added: TraceBehavior[] = [];
    try {
        for (const { entry: { object, type, data } } of ready) {
            const existing = object.getBehavior(type);
            if (existing) {
                applied.push(existing);
                continue;
            }
            const behavior = await object.addBehavior(type, data as never);
            steps.push(new AddBehaviorCommand(object, type.behaviorType, behavior.getData()));
            applied.push(behavior);
            added.push(behavior);
        }
    } catch (error) {
        await rollBack(steps, error);
        throw error;
    }
    const late = await takenBackIfLate(ready, refused, steps);
    if (late) return { applied: [], refused: late, command: null, orphaned: [], warnings: [] };
    announce(added, 'added');
    return { applied, refused, command: steps.length > 0 ? bundle(label('Add', 'to', added, applied), steps) : null, orphaned, warnings: kept(warnings, refused) };
}

/**
 * Commit each patch, in the order given, as one undo step. One that leaves
 * its behavior's data as it is records nothing and copies nothing.
 *
 * Copies a placed reference first and refuses as {@link addBehaviors} does,
 * asking again once the commits have run, and also refuses a behavior that is
 * no longer on its object (`DISPOSED`).
 *
 * A patch that is not an object, that names another behavior's `@type`, or
 * that holds input {@link addBehaviors} rejects throws before anything
 * changes, in any mode. A patch the behavior refuses (`commit` returns errors)
 * throws after the batch is taken back.
 */
export async function commitBehaviors(
    entries: readonly BehaviorCommitEntry[],
    ports: AuthoringPorts = {},
): Promise<BehaviorBatchResult> {
    const warnings: BatchWarning[] = [];
    const inputs = entries.map(({ behavior, patch, fieldKey }, index) => {
        const object = behavior.sceneObject;
        const fitted = fitInput(behavior.name, object, patch, index, warnings);
        if (behavior instanceof BehaviorGraph) assertAuthoredGraph({ ...behavior.getData(), ...fitted });
        return { object, behavior, patch: fitted, fieldKey };
    });
    if (inputs.length === 0) return { applied: [], refused: [], command: null, orphaned: [], warnings };
    const { ready, refused, steps, orphaned } = await prepareBatch(inputs, ports, {
        check: ({ behavior }) => removedRefusal(behavior),
        late: ({ behavior }) => removedRefusal(behavior),
        changes: ({ behavior, patch }) => {
            const now = new Map(Object.entries(behavior.getData()));
            return Object.entries(patch).some(([key, value]) => JSON.stringify(now.get(key)) !== JSON.stringify(value));
        },
    });

    const applied: TraceBehavior[] = [];
    const changed = new Set<TraceBehavior>();
    try {
        for (const { entry: { object, behavior, patch, fieldKey } } of ready) {
            applied.push(behavior);
            const before = behavior.getData();
            const errors = await behavior.commit(patch);
            if (errors.length > 0) {
                throw new Error(`[commitBehaviors] "${behavior.name}" on "${object.name}" refused the patch: ${errors.map(error => error.message).join('; ')}`);
            }
            const after = behavior.getData();
            if (JSON.stringify(after) === JSON.stringify(before)) continue;
            steps.push(new BehaviorCommitCommand(object, behavior.name, before, after, fieldKey ?? null));
            changed.add(behavior);
        }
    } catch (error) {
        await rollBack(steps, error);
        throw error;
    }
    const late = await takenBackIfLate(ready, refused, steps);
    if (late) return { applied: [], refused: late, command: null, orphaned: [], warnings: [] };
    announce([...changed], 'updated');
    return { applied, refused, command: steps.length > 0 ? bundle(label('Edit', 'on', [...changed], applied), steps) : null, orphaned, warnings: kept(warnings, refused) };
}

/**
 * Remove each behavior from its object, as one undo step. Copies a placed
 * reference first and refuses as {@link commitBehaviors} does.
 *
 * A custom script is removed, but undo does not bring it back:
 * `RemoveBehaviorCommand` re-adds through the behavior registry, which holds
 * no scripts.
 */
export async function removeBehaviors(
    behaviors: readonly TraceBehavior[],
    ports: AuthoringPorts = {},
): Promise<BatchResult<TraceBehavior> & { orphaned: string[] }> {
    if (behaviors.length === 0) return { applied: [], refused: [], command: null, orphaned: [] };
    const { ready, refused, steps, orphaned } = await prepareBatch(
        behaviors.map(behavior => ({ object: behavior.sceneObject, behavior })),
        ports,
        { check: ({ behavior }) => removedRefusal(behavior), late: ({ behavior }) => removedRefusal(behavior) },
    );

    const removed: TraceBehavior[] = [];
    try {
        for (const { entry: { object, behavior } } of ready) {
            if (!object.getBehaviors().includes(behavior)) continue;
            const data = behavior.getData();
            // By its type name: a script's class is shared by every script, so the class would match the wrong one.
            object.removeBehavior(behavior.name);
            steps.push(new RemoveBehaviorCommand(object, behavior.name, data));
            removed.push(behavior);
        }
    } catch (error) {
        await rollBack(steps, error);
        throw error;
    }
    announce(removed, 'removed');
    const applied = ready.map(({ entry }) => entry.behavior);
    return { applied, refused, command: steps.length > 0 ? bundle(label('Remove', 'from', removed, applied), steps) : null, orphaned };
}

/**
 * Tell the panels what changed, once the batch stands: the undo a rollback
 * runs is silent, so an event sent before it would describe a change that no
 * longer exists.
 */
function announce(behaviors: readonly TraceBehavior[], action: 'added' | 'updated' | 'removed'): void {
    for (const behavior of behaviors) {
        dispatchBehaviorChanged({ objectPath: behavior.sceneObject.getPath(), behaviorType: behavior.name, action });
    }
}

/**
 * Asked once a batch's awaits are done, as `prepareBatch` asks after a copy:
 * if play started, an undo began or an applied entry's object was deleted
 * meanwhile, take back what the batch did and return every entry refused,
 * each earlier refusal kept. Null when nothing changed under it.
 */
async function takenBackIfLate<E extends { object: SceneObject }>(
    ready: readonly { entry: E; index: number }[],
    refused: readonly BatchRefusal[],
    steps: readonly UndoCommand[],
): Promise<BatchRefusal[] | null> {
    if (ready.length === 0) return null;
    const cause = editRefusal(ready[0].entry.object.traceScene, ready.map(({ entry }) => entry.object));
    if (!cause) return null;
    await rollBack(steps, cause);
    const late = ready.map(({ entry, index }) => ({ ...cause, object: cause.object ?? entry.object, entry: index }));
    return [...refused, ...late].sort((a, b) => a.entry - b.entry);
}

// ── Input ────────────────────────────────────────────────────────────────────

/** A copy of `input` fitted to the format; what fitting dropped goes to `warnings`. Throws on input no fitting can save. */
function fitInput(
    type: string,
    object: SceneObject,
    input: unknown,
    entry: number,
    warnings: BatchWarning[],
): Record<string, unknown> {
    if (typeof input !== 'object' || input === null || Array.isArray(input)) {
        throw new TypeError(`[behaviors] the data for "${type}" on "${object.name}" must be an object, got ${JSON.stringify(input)}`);
    }
    const data = structuredClone(input) as Record<string, unknown>;
    if ('@type' in data && data['@type'] !== type) {
        throw new TypeError(`[behaviors] the data for "${type}" on "${object.name}" names @type ${JSON.stringify(data['@type'])}`);
    }
    if (type === Properties.behaviorType) {
        for (const message of sanitizePropertiesData(data)) warnings.push({ entry, message });
    }
    return data;
}

/** An undo step's name, for what changed; a step that only copied a reference is named for what was applied. */
function label(verb: string, preposition: string, changed: readonly TraceBehavior[], applied: readonly TraceBehavior[]): string {
    const named = changed.length > 0 ? changed : applied;
    return named.length === 1 ? `${verb} ${named[0].name} ${preposition} ${named[0].sceneObject.name}` : `${verb} ${named.length} behaviors`;
}

/** The warnings of the entries that were not refused. */
function kept(warnings: BatchWarning[], refused: readonly { entry: number }[]): BatchWarning[] {
    const out = new Set(refused.map(refusal => refusal.entry));
    return warnings.filter(warning => !out.has(warning.entry));
}

// ── Checks ───────────────────────────────────────────────────────────────────

function removedRefusal(behavior: TraceBehavior): Refusal | null {
    const object = behavior.sceneObject;
    if (object.transform.isDisposed() || object.getBehaviors().includes(behavior)) return null;
    return refuse('DISPOSED', `"${behavior.name}" has been removed from "${object.name}"`, object);
}
